/**
 * The staff portal: leave of absence, and punishment lookup with evidence.
 *
 * Evidence never leaves as a link. It is copied to the clipboard (images) or downloaded (videos, or
 * images if preferred) by the signed-in staff member, to paste or attach into a ticket themselves.
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createLoa,
  deleteEvidence,
  deleteLoa,
  endLoa,
  evidenceFileUrl,
  fetchAccounts,
  fetchEvidence,
  fetchLoa,
  fetchMissingEvidence,
  fetchPortalPunishments,
  importMedalEvidence,
  isMedalLink,
  portalSearchPlayers,
  uploadEvidence,
} from "../api";
import { formatBytes, formatTimestamp } from "../format";
import { confirmDialog } from "./Dialog";
import { useDebounced } from "../hooks/useUrlFilters";
import { can, type Evidence, type LoaEntry, type PortalPunishment, type Tier } from "../types";

interface PortalProps {
  tier: Tier;
  userId: string;
  csrfToken: string;
  /** Player being looked up, kept in the URL so a link from the voice tab lands on them. */
  lookup: string;
  setLookup: (player: string) => void;
  /** Open section, from the URL. Empty shows the home screen of tiles. */
  section: PortalSection | "";
  setSection: (section: PortalSection | "") => void;
}

export type PortalSection = "loa" | "punishments" | "missing";

/** The tiles on the portal home screen, and the titles used when a section is open. */
export const PORTAL_SECTIONS: ReadonlyArray<{ id: PortalSection; title: string; blurb: string; icon: string }> = [
  { id: "loa", title: "Leave of absence", blurb: "Log time away and see who is on LOA", icon: "✈" },
  { id: "punishments", title: "Punishments & evidence", blurb: "Look up a player and attach screenshots or videos", icon: "⚖" },
  { id: "missing", title: "Missing evidence", blurb: "Recent punishments with nothing attached yet", icon: "⚠" },
];

export function PortalTab(props: PortalProps): JSX.Element {
  const { section, setSection } = props;

  return (
    <div className="portal">
      {section === "" && <PortalHome onOpen={setSection} />}
      {section === "loa" && <LoaSection {...props} />}
      {section === "punishments" && <PunishmentSection {...props} />}
      {section === "missing" && (
        <MissingEvidenceSection
          {...props}
          setLookup={(player) => {
            props.setLookup(player);
            setSection("punishments");
          }}
        />
      )}
    </div>
  );
}

// ================================================================================================
// Home screen
// ================================================================================================

function PortalHome({ onOpen }: { onOpen: (section: PortalSection) => void }): JSX.Element {
  // Live counts on the tiles, so the home screen says what needs attention without opening anything.
  const loa = useQuery({ queryKey: ["loa"], queryFn: fetchLoa, refetchInterval: 60_000 });
  const missing = useQuery({ queryKey: ["missing-evidence", 30, ""], queryFn: () => fetchMissingEvidence(30, "") });

  const today = loa.data?.today ?? new Date().toISOString().slice(0, 10);
  const onLoa = (loa.data?.entries ?? []).filter((e) => e.startsOn <= today && e.endsOn >= today).length;
  const missingCount = missing.data?.punishments.length ?? null;

  const badge = (id: PortalSection): string | null => {
    if (id === "loa" && loa.data) return `${onLoa} on LOA now`;
    if (id === "missing" && missingCount !== null) return missingCount === 0 ? "All caught up" : `${missingCount === 500 ? "500+" : missingCount} in 30 days`;
    return null;
  };

  return (
    <div className="portal-home">
      {PORTAL_SECTIONS.map((tile, index) => (
        <button
          key={tile.id}
          type="button"
          className="portal-tile"
          data-tile={tile.id}
          style={{ animationDelay: `${index * 60}ms` }}
          onClick={() => onOpen(tile.id)}
        >
          <span className="portal-tile-icon" aria-hidden="true">
            {tile.icon}
          </span>
          <span className="portal-tile-title">{tile.title}</span>
          <span className="portal-tile-blurb">{tile.blurb}</span>
          {badge(tile.id) && (
            <span className="portal-tile-badge" data-warn={tile.id === "missing" && (missingCount ?? 0) > 0}>
              {badge(tile.id)}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ================================================================================================
// LOA
// ================================================================================================

function LoaSection({ tier, userId, csrfToken }: PortalProps): JSX.Element {
  const queryClient = useQueryClient();
  const loa = useQuery({ queryKey: ["loa"], queryFn: fetchLoa, refetchInterval: 60_000 });

  const today = loa.data?.today ?? new Date().toISOString().slice(0, 10);
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = can(tier, "admin");
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ["loa"] });

  // Admins pick who the LOA is for; the account list is admin-only on the server too.
  const [forUserId, setForUserId] = useState(userId);
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: fetchAccounts, enabled: isAdmin });

  const groups = useMemo(() => {
    const entries = loa.data?.entries ?? [];
    return {
      current: entries.filter((e) => e.startsOn <= today && e.endsOn >= today),
      upcoming: entries.filter((e) => e.startsOn > today).reverse(),
      past: entries.filter((e) => e.endsOn < today).slice(0, 50),
    };
  }, [loa.data, today]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createLoa(startsOn, endsOn, reason, csrfToken, isAdmin && forUserId !== userId ? forUserId : undefined);
      setReason("");
      setEndsOn("");
      setForUserId(userId);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save the LOA.");
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: () => Promise<unknown>): Promise<void> => {
    try {
      await action();
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That did not work.");
    }
  };

  const table = (title: string, entries: LoaEntry[], empty: string, showEnd: boolean): JSX.Element => (
    <>
      <h2 className="section-title">
        {title} <span className="portal-count">{entries.length}</span>
      </h2>
      <div className="tablewrap portal-table">
        <table>
          <thead>
            <tr>
              <th>Staff member</th>
              <th>From</th>
              <th>Until</th>
              <th>Reason</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  {loa.isLoading ? "Loading…" : empty}
                </td>
              </tr>
            )}
            {entries.map((entry) => {
              const mine = entry.userId === userId;
              return (
                <tr key={entry.id}>
                  <td>
                    <b>{entry.username}</b>
                    {mine && <span className="portal-you"> (you)</span>}
                  </td>
                  <td className="mono">{entry.startsOn}</td>
                  <td className="mono">{entry.endsOn}</td>
                  <td style={{ whiteSpace: "normal" }}>{entry.reason}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {(mine || isAdmin) && showEnd && (
                      <button className="btn" type="button" onClick={() => void act(() => endLoa(entry.id, csrfToken))}>
                        {mine ? "I'm back" : "End now"}
                      </button>
                    )}{" "}
                    {(mine || isAdmin) && (
                      <button
                        className="btn"
                        type="button"
                        onClick={async () => {
                          const ok = await confirmDialog({
                            title: `Delete ${entry.username}'s LOA?`,
                            message: `${entry.startsOn} to ${entry.endsOn}: "${entry.reason}"`,
                            confirmLabel: "Delete LOA",
                            danger: true,
                          });
                          if (ok) void act(() => deleteLoa(entry.id, csrfToken));
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <>
      <h2 className="section-title">{isAdmin ? "Put someone on LOA" : "Log an LOA"}</h2>
      <form className="filterbar portal-form" onSubmit={submit}>
        {isAdmin && (
          <label className="portal-field">
            Who
            <select
              className="input"
              value={forUserId}
              onChange={(e) => setForUserId(e.target.value)}
              aria-label="Staff member to put on LOA"
            >
              <option value={userId}>Me</option>
              {(accounts.data?.users ?? [])
                .filter((account) => account.id !== userId)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.username} ({account.tier})
                  </option>
                ))}
            </select>
          </label>
        )}
        <label className="portal-field">
          From
          <input className="input" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </label>
        <label className="portal-field">
          Until
          <input className="input" type="date" value={endsOn} min={startsOn} onChange={(e) => setEndsOn(e.target.value)} />
        </label>
        <input
          className="input grow"
          placeholder="Reason (e.g. holiday, exams)"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          aria-label="Reason"
        />
        <button className="btn" data-variant="primary" type="submit" disabled={busy || !startsOn || !endsOn || !reason.trim()}>
          Save LOA
        </button>
      </form>
      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      {table("Currently on LOA", groups.current, "Nobody is on LOA right now.", true)}
      {table("Upcoming", groups.upcoming, "No upcoming LOA.", true)}
      {table("Past", groups.past, "No past LOA.", false)}
    </>
  );
}

// ================================================================================================
// Missing evidence
// ================================================================================================

function MissingEvidenceSection({ setLookup }: PortalProps): JSX.Element {
  const [days, setDays] = useState(30);
  const [issuer, setIssuer] = useState("");
  const debouncedIssuer = useDebounced(issuer);

  const missing = useQuery({
    queryKey: ["missing-evidence", days, debouncedIssuer],
    queryFn: () => fetchMissingEvidence(days, debouncedIssuer),
  });
  const rows = missing.data?.punishments ?? [];

  return (
    <>
      <p className="users-note">
        Bans, mutes and kicks with no screenshot or video attached. Click one to open that player and add
        the evidence.
      </p>
      <div className="filterbar">
        <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Time range">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
        <input
          className="input"
          placeholder="Issued by (in-game name)"
          value={issuer}
          onChange={(e) => setIssuer(e.target.value)}
          aria-label="Issued by"
          style={{ width: "24ch" }}
        />
        <span className="grow" />
        <span className="portal-missing-count" data-zero={rows.length === 0}>
          {missing.isLoading ? "…" : rows.length === 500 ? "500+" : rows.length} missing
        </span>
      </div>

      <div className="tablewrap portal-table">
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Player</th>
              <th>Issued</th>
              <th>By</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {missing.isLoading && (
              <tr>
                <td colSpan={5} className="empty">
                  Loading…
                </td>
              </tr>
            )}
            {missing.data && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  Every punishment in this range has evidence. Nice.
                </td>
              </tr>
            )}
            {rows.map((p) => (
              <tr
                key={`${p.kind}:${p.id}`}
                className="portal-row"
                style={{ cursor: p.player ? "pointer" : "default" }}
                onClick={() => p.player && setLookup(p.player)}
              >
                <td>
                  <span className="portal-kind" data-kind={p.kind}>
                    {KIND_LABEL[p.kind]}
                  </span>
                </td>
                <td>
                  <b>{p.player ?? "unknown"}</b>
                </td>
                <td className="mono">{formatTimestamp(p.issued_at)}</td>
                <td>{p.issuer}</td>
                <td style={{ whiteSpace: "normal" }}>{p.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ================================================================================================
// Punishments & evidence
// ================================================================================================

const KIND_LABEL: Record<string, string> = { ban: "Ban", mute: "Mute", kick: "Kick" };

function PunishmentSection({ tier, userId, csrfToken, lookup, setLookup }: PortalProps): JSX.Element {
  const [term, setTerm] = useState(lookup);
  const debounced = useDebounced(term);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => setTerm(lookup), [lookup]);

  const suggestions = useQuery({
    queryKey: ["portal-search", debounced],
    queryFn: ({ signal }) => portalSearchPlayers(debounced, signal),
    enabled: debounced.trim().length >= 2 && debounced !== lookup,
  });

  const punishments = useQuery({
    queryKey: ["portal-punishments", lookup],
    queryFn: () => fetchPortalPunishments(lookup),
    enabled: Boolean(lookup),
  });

  return (
    <>
      <form
        className="filterbar"
        onSubmit={(event) => {
          event.preventDefault();
          setOpen(null);
          setLookup(term.trim());
        }}
      >
        <input
          className="input"
          placeholder="Player name or UUID"
          value={term}
          list="portal-player-suggestions"
          onChange={(e) => setTerm(e.target.value)}
          aria-label="Player"
          style={{ width: "28ch" }}
        />
        <datalist id="portal-player-suggestions">
          {suggestions.data?.players.map((name) => <option key={name} value={name} />)}
        </datalist>
        <button className="btn" data-variant="primary" type="submit" disabled={!term.trim()}>
          Look up
        </button>
      </form>

      {!lookup && <p className="users-note">Search for a player to see their punishments and the evidence for each.</p>}

      {lookup && (
        <div className="tablewrap portal-table">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Issued</th>
                <th>By</th>
                <th>Reason</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {punishments.isLoading && (
                <tr>
                  <td colSpan={5} className="empty">
                    Loading…
                  </td>
                </tr>
              )}
              {punishments.data && punishments.data.punishments.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty">
                    No punishments on record for {lookup}.
                  </td>
                </tr>
              )}
              {punishments.data?.punishments.map((p, index) => {
                const key = `${p.kind}:${p.id ?? `none-${index}`}`;
                const isOpen = open === key;
                return (
                  <PunishmentRow
                    key={key}
                    punishment={p}
                    player={punishments.data?.player ?? lookup}
                    isOpen={isOpen}
                    onToggle={() => setOpen(isOpen ? null : key)}
                    tier={tier}
                    userId={userId}
                    csrfToken={csrfToken}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {punishments.data && !punishments.data.kickHistoryAvailable && (
        <p className="users-note">Kick history is limited on this server, so older kicks may be missing and cannot hold evidence.</p>
      )}
    </>
  );
}

function PunishmentRow({
  punishment,
  player,
  isOpen,
  onToggle,
  tier,
  userId,
  csrfToken,
}: {
  punishment: PortalPunishment;
  player: string;
  isOpen: boolean;
  onToggle: () => void;
  tier: Tier;
  userId: string;
  csrfToken: string;
}): JSX.Element {
  return (
    <>
      <tr className="portal-row" data-open={isOpen} onClick={onToggle} style={{ cursor: "pointer" }}>
        <td>
          <span className="portal-kind" data-kind={punishment.kind}>
            {KIND_LABEL[punishment.kind]}
          </span>
          {punishment.active && <span className="portal-active">active</span>}
        </td>
        <td className="mono">{formatTimestamp(punishment.issued_at)}</td>
        <td>{punishment.issuer}</td>
        <td style={{ whiteSpace: "normal" }}>{punishment.reason}</td>
        <td>
          {punishment.id === null ? (
            <span className="users-note">n/a</span>
          ) : (
            <button
              type="button"
              className="btn"
              data-missing={punishment.evidenceCount === 0}
              title={punishment.evidenceCount === 0 ? "No evidence has been uploaded for this punishment" : undefined}
              onClick={(e) => (e.stopPropagation(), onToggle())}
            >
              {punishment.evidenceCount > 0
                ? `${punishment.evidenceCount} file${punishment.evidenceCount === 1 ? "" : "s"}`
                : "⚠ No evidence"}
              {isOpen ? " ▴" : " ▾"}
            </button>
          )}
        </td>
      </tr>
      {isOpen && punishment.id !== null && (
        <tr className="portal-evidence-row">
          <td colSpan={5}>
            <EvidencePanel punishment={punishment} player={player} tier={tier} userId={userId} csrfToken={csrfToken} />
          </td>
        </tr>
      )}
    </>
  );
}

function EvidencePanel({
  punishment,
  player,
  tier,
  userId,
  csrfToken,
}: {
  punishment: PortalPunishment;
  player: string;
  tier: Tier;
  userId: string;
  csrfToken: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const id = punishment.id as string;
  const evidence = useQuery({
    queryKey: ["evidence", punishment.kind, id],
    queryFn: () => fetchEvidence(punishment.kind, id),
  });

  const fileInput = useRef<HTMLInputElement | null>(null);
  const [note, setNote] = useState("");
  const [medalLink, setMedalLink] = useState("");
  const [status, setStatus] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["evidence", punishment.kind, id] });
    await queryClient.invalidateQueries({ queryKey: ["portal-punishments"] });
    await queryClient.invalidateQueries({ queryKey: ["missing-evidence"] });
  };

  const upload = async (files: FileList | File[]): Promise<void> => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setBusy(true);
    setStatus(null);
    try {
      for (const file of list) {
        await uploadEvidence(punishment.kind, id, file, note, csrfToken);
      }
      setNote("");
      if (fileInput.current) fileInput.current.value = "";
      setStatus({ kind: "ok", text: `Uploaded ${list.length} file${list.length === 1 ? "" : "s"}.` });
      await refresh();
    } catch (caught) {
      setStatus({ kind: "error", text: caught instanceof Error ? caught.message : "Upload failed." });
    } finally {
      setBusy(false);
    }
  };

  const importMedal = async (link: string): Promise<void> => {
    if (!isMedalLink(link)) {
      setStatus({ kind: "error", text: "That is not a medal.tv link." });
      return;
    }
    setBusy(true);
    setStatus({ kind: "ok", text: "Downloading the clip from Medal…" });
    try {
      await importMedalEvidence(punishment.kind, id, link, note, csrfToken);
      setNote("");
      setMedalLink("");
      setStatus({ kind: "ok", text: "Medal clip downloaded and uploaded." });
      await refresh();
    } catch (caught) {
      setStatus({ kind: "error", text: caught instanceof Error ? caught.message : "Could not import the Medal clip." });
    } finally {
      setBusy(false);
    }
  };

  const details = (): string => {
    const files = evidence.data?.evidence ?? [];
    return [
      `Player: ${player}`,
      `Punishment: ${KIND_LABEL[punishment.kind]}${punishment.active ? " (active)" : ""}`,
      `Reason: ${punishment.reason}`,
      `Issued by: ${punishment.issuer} on ${formatTimestamp(punishment.issued_at)}`,
      punishment.expires_at ? `Expires: ${formatTimestamp(punishment.expires_at)}` : null,
      files.length > 0 ? `Evidence: ${files.map((f) => f.fileName).join(", ")}` : "Evidence: none attached",
    ]
      .filter(Boolean)
      .join("\n");
  };

  const copyDetails = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(details());
      setStatus({ kind: "ok", text: "Punishment details copied — paste them into the ticket." });
    } catch {
      setStatus({ kind: "error", text: "Your browser blocked clipboard access." });
    }
  };

  return (
    <div
      className="evidence-panel"
      // Paste a screenshot straight in with Ctrl+V.
      onPaste={(event) => {
        const files = Array.from(event.clipboardData.files).filter((f) => /^(image|video)\//.test(f.type));
        if (files.length > 0) {
          event.preventDefault();
          void upload(files.map((f, i) => (f.name && f.name !== "image.png" ? f : new File([f], `pasted-${Date.now()}-${i}.png`, { type: f.type }))));
          return;
        }
        // A Medal link pasted anywhere outside a text field is imported straight away.
        const text = event.clipboardData.getData("text");
        const target = event.target as HTMLElement;
        if (!busy && isMedalLink(text) && target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          event.preventDefault();
          void importMedal(text);
        }
      }}
      tabIndex={-1}
    >
      <div className="evidence-actions">
        <button className="btn" type="button" onClick={() => void copyDetails()}>
          Copy punishment details
        </button>
        <span className="grow" />
        <input
          className="input"
          placeholder="Note (optional)"
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Evidence note"
        />
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
          multiple
          hidden
          onChange={(e) => e.target.files && void upload(e.target.files)}
        />
        <button className="btn" data-variant="primary" type="button" disabled={busy} onClick={() => fileInput.current?.click()}>
          {busy ? "Uploading…" : "Upload evidence"}
        </button>
      </div>
      <form
        className="evidence-actions"
        onSubmit={(event) => {
          event.preventDefault();
          if (medalLink.trim()) void importMedal(medalLink);
        }}
      >
        <span className="grow" />
        <input
          className="input"
          type="url"
          placeholder="https://medal.tv/clips/…"
          value={medalLink}
          onChange={(e) => setMedalLink(e.target.value)}
          aria-label="Medal clip link"
        />
        <button className="btn" type="submit" disabled={busy || !medalLink.trim()}>
          Add Medal clip
        </button>
      </form>
      <p className="users-note">
        Tip: click here and press Ctrl+V to upload a screenshot straight from your clipboard. Medal links are downloaded
        and saved as a normal video file.
      </p>

      {status && (
        <div className={status.kind === "error" ? "login-error" : "evidence-ok"} role="status">
          {status.text}
        </div>
      )}

      {evidence.isLoading && <p className="users-note">Loading evidence…</p>}
      {evidence.data && evidence.data.evidence.length === 0 && <p className="users-note">No evidence uploaded for this punishment yet.</p>}

      <div className="evidence-grid">
        {evidence.data?.evidence.map((item) => (
          <EvidenceCard
            key={item.id}
            item={item}
            canDelete={item.uploadedById === userId || can(tier, "admin")}
            csrfToken={csrfToken}
            onDeleted={() => void refresh()}
            onStatus={setStatus}
          />
        ))}
      </div>
    </div>
  );
}

/** Re-encodes any image as PNG, the one image type the async clipboard API reliably accepts. */
async function toPngBlob(blob: Blob): Promise<Blob> {
  if (blob.type === "image/png") return blob;
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob((png) => (png ? resolve(png) : reject(new Error("could not convert image"))), "image/png"),
  );
}

function EvidenceCard({
  item,
  canDelete,
  csrfToken,
  onDeleted,
  onStatus,
}: {
  item: Evidence;
  canDelete: boolean;
  csrfToken: string;
  onDeleted: () => void;
  onStatus: (status: { kind: "ok" | "error"; text: string }) => void;
}): JSX.Element {
  const isImage = item.contentType.startsWith("image/");
  const src = evidenceFileUrl(item.id);

  const copyImage = async (): Promise<void> => {
    try {
      // The ClipboardItem is given a promise so the write starts inside the click, which Safari needs.
      const png = fetch(src, { credentials: "same-origin" })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.blob();
        })
        .then(toPngBlob);
      await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
      onStatus({ kind: "ok", text: `Copied ${item.fileName} — paste it into the ticket with Ctrl+V.` });
    } catch {
      onStatus({ kind: "error", text: "Could not copy the image. Use Download instead." });
    }
  };

  const download = (): void => {
    // A same-origin, session-authorised download. Nothing is published or shareable.
    const a = document.createElement("a");
    a.href = evidenceFileUrl(item.id, true);
    a.download = item.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <figure className="evidence-card">
      {isImage ? (
        <a href={src} target="_blank" rel="noreferrer" className="evidence-media">
          <img src={src} alt={item.fileName} loading="lazy" />
        </a>
      ) : (
        <video className="evidence-media" src={src} controls preload="metadata" />
      )}
      <figcaption>
        <b title={item.fileName}>{item.fileName}</b>
        <span>
          {formatBytes(item.bytes)} · {item.uploadedBy} · {formatTimestamp(item.uploadedAt)}
        </span>
        {item.note && <span className="evidence-note">{item.note}</span>}
        <div className="evidence-buttons">
          {isImage && (
            <button className="btn" data-variant="primary" type="button" onClick={() => void copyImage()}>
              Copy image
            </button>
          )}
          <button className="btn" type="button" onClick={download}>
            {isImage ? "Download" : "Download video"}
          </button>
          {canDelete && (
            <button
              className="btn"
              type="button"
              onClick={async () => {
                const ok = await confirmDialog({
                  title: `Delete ${item.fileName}?`,
                  message: "The file is removed from the server for good. This cannot be undone.",
                  confirmLabel: "Delete evidence",
                  danger: true,
                });
                if (!ok) return;
                try {
                  await deleteEvidence(item.id, csrfToken);
                  onDeleted();
                } catch (caught) {
                  onStatus({ kind: "error", text: caught instanceof Error ? caught.message : "Delete failed." });
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      </figcaption>
    </figure>
  );
}
